import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnInit, Optional, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { CrmConfig, EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { Odds, OddsFormatterService, OddsOperations } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, filter, map, of, startWith, switchMap, withLatestFrom } from 'rxjs';

import { TokensCalculatorService } from '../../../../../lib/tokens-base/tokens-calculator.service';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BetslipUnknownPick } from '../../../../core/picks/betslip-unknown-pick';
import { PickOddsState, PriceType } from '../../../../core/picks/pick-models';
import { BETSLIP_TYPE_PROVIDER } from '../../../../digital/services/betslip-type-provider.service';
import { ODDS_VIEW_MODE } from '../../../../integration/sports-injection-services';
import { QuickBetPickBoostedOddsProviderService } from '../../../quick-bet/components/result/quick-bet-odds-result.service';
import { QuickBetPickPlacedOddsProviderService } from '../../../quick-bet/components/result/quickbet-pick-placed-odds-provider.service';
import { oddsBoostAnimations } from '../../../reward-tokens/components/digital/odds-boost-animations';
import { OddsBoostToken } from '../../../reward-tokens/reward-tokens.model';
import { selectOddsBoostTokenFactory } from '../../../reward-tokens/selectors';
import { BestOddsPickEligibilityValidator } from '../../../reward-tokens/services/validators/best-odds-guaranteed/best-odds-pick-eligibility-validator.service';
import { singleBetPickSelectorFactory } from '../../../single-bet/selectors';
import { betslipSelectedTypeStateSelector, betslipTypeStateSelector, editBetPickSelectorFactory } from '../../../types/selectors';
import BetslipPickUtilsService from '../../services/betslip-pick-utils.service';

interface PickOddsViewModel {
    pick: BetslipPick;
    boostedPrice: string | null;
    oddsAreUp: boolean;
    oddsAreDown: boolean;
    currentPrice: string;
    priceBoostOdds: string | null;
    isPriceBoosted: boolean;
    bogVisible: boolean;
    isPickLoading?: boolean;
}

@Component({
    selector: 'bs-digital-pick-odds',
    templateUrl: './pick-odds.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./pick-odds.component.scss', './pick-odds.component.linear.scss'],
    animations: [oddsBoostAnimations],
    standalone: false,
})
export default class PickOddsComponent implements OnInit {
    @HostBinding('class') class = 'betslip-pick-odds reward-tokens-module';

    @Input() pick$!: Observable<BetslipPick>; // WTF is this an observable?
    @Input() teasedValue?: string;
    oddsDisplayFormat = input.required<OddsDisplayFormat>();

    oddsStateType = PickOddsState;
    isEpcotEnabled = this.epcotConfig.isEnabled;
    readonly messages: {
        locked: string;
        closed: string;
        startingPrice: string;
        teaserPoints?: string;
    };

    vm$: Observable<PickOddsViewModel>;
    private pickAndIsSelectedPick$: Observable<{ pick: any; isSelected: boolean }>;
    private oddsDisplayFormat$ = toObservable(this.oddsDisplayFormat);

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private oddsFormatterSerice: OddsFormatterService,
        private betslipPickUtils: BetslipPickUtilsService,
        protected store: Store<IBetslipRootState>,
        private bogService: BestOddsPickEligibilityValidator,
        private epcotConfig: EpcotConfig,
        private tokensCalculator: TokensCalculatorService,
        @Optional() private quickBetBoostedOddsProvider: QuickBetPickBoostedOddsProviderService | null,
        @Optional() private quickBetPickPlacedOddsProvider: QuickBetPickPlacedOddsProviderService | null,
        @Inject(ODDS_VIEW_MODE) @Optional() private resultView: boolean | null,
        protected crmConfig: CrmConfig,
        @Inject(BETSLIP_TYPE_PROVIDER) private betslipType$: Observable<BetslipType>,
    ) {
        const { common, teaserBet } = messageProvider.getMessages();
        this.messages = {
            locked: common.LockedPick,
            closed: common.ClosedPick,
            startingPrice: common.StartingPrice,
            teaserPoints: teaserBet?.messages.Points,
        };
    }

    ngOnInit() {
        this.initPickObservables();

        if (this.resultView) {
            this.vm$ = this.getOddsResultModeVm$();
        } else {
            this.vm$ = this.getVm$();
        }
    }

    isPriceBoost(pick: BetslipPick): boolean {
        return pick.boostedPriceDetails?.isPriceBoosted!;
    }

    private getOddsResultModeVm$() {
        //Probably the line below should be changed for result mode, further testing required.
        const hasBogAvailable$ = this.getBogAvailability$();

        return combineLatest([
            this.getResultBoostedOdds$(),
            this.pick$,
            hasBogAvailable$,
            this.quickBetPickPlacedOddsProvider!.odds$,
            this.oddsDisplayFormat$,
        ]).pipe(
            map(([boostedOdds, pick, bogVisible, odds]) => {
                return {
                    pick,
                    boostedPrice: boostedOdds,
                    oddsAreUp: false,
                    oddsAreDown: false,
                    currentPrice: this.getFormattedOdds(odds, pick),
                    priceBoostOdds: this.priceBoostOdds(pick),
                    isPriceBoosted: this.isPriceBoost(pick),
                    bogVisible,
                };
            }),
        );
    }

    private getVm$() {
        const hasBogAvailable$ = this.getBogAvailability$();

        return combineLatest([this.getBoostedOdds$(), this.pick$, hasBogAvailable$, this.oddsDisplayFormat$]).pipe(
            map(([boostedOdds, pick, bogVisible]) => {
                return {
                    pick,
                    isPickLoading: BetslipUnknownPick.isPick(pick),
                    boostedPrice: boostedOdds,
                    oddsAreUp: this.oddsAreUp(pick),
                    oddsAreDown: this.oddsAreDown(pick),
                    currentPrice: this.currentPrice(pick),
                    priceBoostOdds: this.priceBoostOdds(pick),
                    isPriceBoosted: this.isPriceBoost(pick),
                    bogVisible,
                };
            }),
        );
    }

    private getFormattedOdds(odds: Odds | null, pick: BetslipPick) {
        if (odds) {
            return this.oddsFormatterSerice.format(odds, { displayFormat: this.oddsDisplayFormat() });
        }

        return pick.sportId === SportConstant.Horses ? this.currentPrice(pick) : this.messages.startingPrice;
    }

    private initPickObservables() {
        const isInEmb$ = this.store
            .select(betslipTypeStateSelector)
            .pipe(map((typeState) => typeState.base.currentSelectedType === BetslipType.EditBet));

        this.pickAndIsSelectedPick$ = combineLatest([this.pick$, isInEmb$]).pipe(
            filter(isDefined),
            switchMap(([pick, isInEmb]) => {
                return isInEmb ? this.getEditBetPick(pick) : this.getSingleBetPick(pick);
            }),
        );
    }

    private getResultBoostedOdds$(): Observable<string | null> {
        return this.quickBetBoostedOddsProvider!.boostedOdds$.pipe(
            map((boostedOdds) =>
                this.oddsFormatterSerice.format(boostedOdds, {
                    displayFormat: this.oddsDisplayFormat(),
                    cutZero: true,
                    isSummary: false,
                    maxDecimalPlaces: this.crmConfig.tokens.boostedOddsMaxDecimalPlaces,
                    useOddsMultiplicationFormat: true,
                }),
            ),
            startWith(null),
        );
    }

    private getBoostedOdds$(): Observable<string | null> {
        return this.pickAndIsSelectedPick$.pipe(
            withLatestFrom(this.betslipType$),
            switchMap(([{ pick }, betslipType]) => {
                if (betslipType === BetslipType.Single) {
                    return this.store.select(selectOddsBoostTokenFactory({ pickId: pick.id, betslipType: BetslipType.Single })).pipe(
                        startWith(null),
                        map((token) => this.getBoostedPrice(token, pick)),
                    );
                }

                if (betslipType === BetslipType.BetBuilder) {
                    return this.store.select(selectOddsBoostTokenFactory({ pickId: pick.id, betslipType: BetslipType.BetBuilder })).pipe(
                        startWith(null),
                        map((token) => this.getBoostedPrice(token, pick)),
                    );
                }

                return of(null);
            }),
        );
    }

    private getBogAvailability$(): Observable<boolean> {
        return this.pickAndIsSelectedPick$.pipe(switchMap((t) => this.bogService.isBogEligiblePick$(t.pick)));
    }

    private getEditBetPick(pick: BetslipPick): Observable<{ pick: any; isSelected: boolean }> {
        return this.store.select(editBetPickSelectorFactory(pick.id)).pipe(
            filter(isDefined),
            map((t) => ({ pick, isSelected: false })),
        );
    }

    private getSingleBetPick(pick: BetslipPick): Observable<{ pick: any; isSelected: boolean }> {
        return this.store.select(betslipSelectedTypeStateSelector).pipe(
            switchMap((selectedType) => {
                if (selectedType === BetslipType.Single) {
                    return this.store
                        .select(singleBetPickSelectorFactory(pick.id))
                        .pipe(map((pickState) => ({ pick, isSelected: pickState?.isSelected ?? false })));
                } else {
                    return of({ pick, isSelected: false });
                }
            }),
            startWith({ pick, isSelected: false }),
        );
    }

    private currentPrice(pick: BetslipPick): string {
        if (!pick.currentPrice) {
            return '';
        }
        if (BetslipBetBuilderPick.isEntainUiBetBuilderPick(pick) && !OddsOperations.isOddsValid(pick.currentPrice.nativeOdds)) {
            return '';
        }

        switch (pick.currentPrice.type) {
            case PriceType.Fixed: {
                return this.oddsFormatterSerice.format(pick.currentPrice.nativeOdds, { displayFormat: this.oddsDisplayFormat() });
            }
            case PriceType.StartingPrice: {
                return this.messages.startingPrice;
            }
            default: {
                return '';
            }
        }
    }

    private getBoostedPrice(token: OddsBoostToken | null, pick: BetslipPick): string | null {
        if (!pick.currentPrice || !token) {
            return null;
        }

        const price = OddsOperations.toCalculatedOdds(pick.currentPrice.nativeOdds);

        const boostedOdds = this.tokensCalculator.getBoostedNativeOdds2(price, token.boostFactor, token.oddsBoostFormula, this.oddsDisplayFormat());

        return this.oddsFormatterSerice.format(boostedOdds, {
            displayFormat: this.oddsDisplayFormat(),
            cutZero: true,
            isSummary: false,
            maxDecimalPlaces: this.crmConfig.tokens.boostedOddsMaxDecimalPlaces,
            useOddsMultiplicationFormat: true,
        });
    }

    private priceBoostOdds(pick: BetslipPick): string | null {
        return this.oddsFormatterSerice.format(pick.boostedPriceDetails?.boostedPrice?.nativeOdds, {
            displayFormat: this.oddsDisplayFormat(),
        });
    }

    private oddsAreUp(pick: BetslipPick): boolean {
        return this.betslipPickUtils.compareAgainstLastPrice(pick) > 0;
    }

    private oddsAreDown(pick: BetslipPick): boolean {
        return this.betslipPickUtils.compareAgainstLastPrice(pick) < 0;
    }
}
